var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * VA-28160 — proctoring threshold calibration bench (admin).
 *
 * WHAT IT IS FOR
 *
 * The detection thresholds decide what "deviated from centre" means, and no
 * number chosen at a desk survives contact with a real face, a real webcam and
 * a real room. This records a short session, keeps every frame's measured
 * values, and then lets an operator move the thresholds against the playback
 * and watch what each setting WOULD have flagged.
 *
 * WHY REPLAY WORKS AT ALL
 *
 * The expensive half — MediaPipe — runs once, while recording. What is stored
 * per frame is the derived geometry: deviation from centre, bearing, the
 * landmarks. Changing a threshold afterwards is then arithmetic over numbers we
 * already have, so a slider can re-evaluate an entire recording between frames.
 * Record once, tune fifty times.
 *
 * WHY THE GEOMETRY IS NOT COMPUTED HERE
 *
 * Deviation and bearing come from the BACKEND, in the detect result. Computing
 * them in the browser would mean a second implementation of the head-pose and
 * gaze maths, and the two would drift — at which point the bench calibrates
 * against arithmetic the live detector does not use, which is worse than having
 * no bench. The browser only applies thresholds to values it was given.
 *
 * WHAT IS NOT KEPT
 *
 * Nothing. The recording is an in-memory Blob and the frames are an array; both
 * die with the page. The ONLY thing that persists is the threshold values, and
 * only when the operator presses save. A calibration tool that quietly
 * accumulated recordings of people's faces would be a liability, not a feature.
 */
import { useConfigContext } from '@/hoc/context/config-provider';
import { useToast } from '@/hoc';
import { AlertTriangle, Camera, Crosshair, Loader2, Play, Save, Square, Pause, RotateCcw, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../ui';
import { thresholdDiff } from './proctoring-threshold-diff';
/**
 * Confirmation before the thresholds are committed.
 *
 * WHY THIS IS NOT FUSSY
 *
 * These three numbers decide what every FUTURE interview reports. A slider is
 * the right control for exploring them against a replay — that is the whole
 * point of the bench — but the same gesture that explores is the one that
 * publishes, and the two deserve different weights. Saving an eye deviation of
 * 0.04 is what put "100% of the interview spent looking away" on a real report;
 * nothing in the flow asked whether that was meant.
 *
 * It shows the DIFF rather than asking "are you sure": what is changing, from
 * what, to what. A confirmation that does not say what it is confirming trains
 * people to dismiss it.
 */
const ConfirmThresholds = ({ rows, onCancel, onConfirm, isSaving, }) => (_jsx("div", { className: "fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4", children: _jsxs("div", { className: "w-full max-w-md overflow-hidden rounded-2xl border border-dash-border bg-dash-card shadow-2xl", children: [_jsxs("header", { className: "flex items-start justify-between gap-4 border-b border-dash-border px-5 py-4", children: [_jsxs("div", { className: "flex items-start gap-2.5", children: [_jsx(AlertTriangle, { className: "mt-0.5 h-4 w-4 shrink-0 text-amber-500" }), _jsxs("div", { children: [_jsx("h3", { className: "text-sm font-semibold text-dash-text", children: "Apply these thresholds to every future interview?" }), _jsx("p", { className: "mt-0.5 text-xs text-dash-muted", children: "Reports already generated keep the settings they were measured with." })] })] }), _jsx("button", { type: "button", onClick: onCancel, "aria-label": "Close", className: "rounded-md p-1.5 text-dash-muted hover:bg-dash-hover hover:text-dash-text", children: _jsx(X, { className: "h-4 w-4" }) })] }), _jsx("div", { className: "px-5 py-4", children: _jsx("table", { className: "w-full text-sm", children: _jsx("tbody", { children: rows.map((r) => (_jsxs("tr", { className: "border-b border-dash-border/60 last:border-0", children: [_jsx("td", { className: "py-1.5 pr-3 text-dash-muted", children: r.label }), _jsx("td", { className: "py-1.5 text-right font-mono text-xs text-dash-muted line-through", children: r.from }), _jsx("td", { className: "w-6 py-1.5 text-center text-dash-muted", children: "\u2192" }), _jsx("td", { className: "py-1.5 text-right font-mono text-xs font-semibold text-dash-text", children: r.to })] }, r.label))) }) }) }), _jsxs("footer", { className: "flex justify-end gap-2 border-t border-dash-border px-5 py-3", children: [_jsx("button", { type: "button", onClick: onCancel, className: "rounded-md border border-dash-border px-3 py-1.5 text-sm font-medium text-dash-text hover:bg-dash-hover", children: "Cancel" }), _jsxs("button", { type: "button", onClick: onConfirm, disabled: isSaving, className: "inline-flex items-center gap-1.5 rounded-md bg-dash-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-dash-accentHover disabled:opacity-50", children: [isSaving && _jsx(Loader2, { className: "h-3.5 w-3.5 animate-spin" }), "Apply"] })] })] }) }));
import { useProctoringConfigAdmin, useUpdateProctoringConfig, } from './voice-admin-api';
/** Frames per second sent to the detector while recording. */
const DETECT_FPS = 5;
/** A result older than this is not a reading, it is a memory. Generous against
 *  the 5/s cadence, so ordinary jitter never blanks the panel. */
const RESULT_STALE_MS = 1500;
const WIDTH_OPTIONS = [320, 480, 640, 720, 960, 1280];
/** MediaPipe pose landmark indices we care about (33-point model). */
const POSE_EDGES = [
    [11, 12], [11, 13], [13, 15], [12, 14], [14, 16], // shoulders + arms
    [11, 23], [12, 24], [23, 24], // torso
];
/** Hand skeleton, 21-point model: palm then five fingers. */
const HAND_EDGES = [
    [0, 1], [1, 2], [2, 3], [3, 4],
    [0, 5], [5, 6], [6, 7], [7, 8],
    [0, 9], [9, 10], [10, 11], [11, 12],
    [0, 13], [13, 14], [14, 15], [15, 16],
    [0, 17], [17, 18], [18, 19], [19, 20],
    [5, 9], [9, 13], [13, 17],
];
/** The iris rings `refine_landmarks` adds — drawn as circles, since where the
 *  iris sits inside the eye IS the gaze signal. */
const LEFT_IRIS = [468, 469, 470, 471, 472];
const RIGHT_IRIS = [473, 474, 475, 476, 477];
/** The bench's socket sits beside the interview one, not under it.
 *
 * A first version appended `/ws/admin/proctoring/test/` to the base and silently
 * never connected, so the panel showed a camera, recorded a video, and detected
 * absolutely nothing — with no error anywhere, because the failure was swallowed.
 * The route is a SUBSTITUTION on the interview path. */
const proctoringWsUrl = (base) => (base || '').replace(/ws\/interview\/?$/, 'ws/proctoring-test/');
/** Compass bearing → a word a person can read. */
const bearingWord = (deg) => {
    if (deg == null)
        return '';
    const dirs = ['up', 'up-right', 'right', 'down-right', 'down', 'down-left', 'left', 'up-left'];
    return dirs[Math.round(((deg % 360) + 360) % 360 / 45) % 8];
};
const ProctoringCalibrationPanel = () => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
    const { showToast } = useToast();
    // Nested under `config`, not top level — a flat destructure yields undefined
    // and the socket never opens.
    const { config: { webSocketUrl } = {} } = useConfigContext();
    const { data: config } = useProctoringConfigAdmin();
    const updateConfig = useUpdateProctoringConfig();
    const videoRef = useRef(null); // live camera
    const replayRef = useRef(null); // recorded playback
    const canvasRef = useRef(null); // mesh + labels
    const frameCanvasRef = useRef(null);
    const wsRef = useRef(null);
    const streamRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const samplesRef = useRef([]);
    const recStartRef = useRef(0);
    const latestRef = useRef(null);
    // When the last result arrived. A reading with no timestamp cannot be told
    // apart from a reading that stopped arriving, and the panel showed a frozen
    // "measured now" for a full minute after the socket dropped — dead data
    // presented as live, which is the one thing a proctoring tool must not do.
    const lastResultAtRef = useRef(0);
    const [camState, setCamState] = useState('idle');
    const [wsState, setWsState] = useState('idle');
    // The candidate's own neutral. Deviation is only meaningful relative to it:
    // measured from an absolute zero, a face at a natural laptop-camera angle
    // reads ~35 degrees "down" while looking straight at the lens.
    const [centred, setCentred] = useState(false);
    // Which pipeline the bench drives. Both are kept switchable rather than one
    // replacing the other: they disagree about head pose, and deciding which to
    // believe is the whole reason for measuring them on the same camera.
    const [engine, setEngine] = useState('tasks');
    const engineRef = useRef(engine);
    engineRef.current = engine;
    const [layers, setLayers] = useState({
        mesh: true, iris: true, pose: true, hands: true, gaze: true, dial: true,
    });
    const layersRef = useRef(layers);
    layersRef.current = layers;
    const baselineRef = useRef(null);
    const [camError, setCamError] = useState('');
    // Kept apart from `camError` on purpose. They have different lifetimes: a
    // camera failure persists until the operator acts, while a socket failure is
    // routinely transient and self-heals on the next reconnect tick. Sharing one
    // field left a red "socket failed" sitting under a green "connected" — a
    // contradiction the reader has to arbitrate, which is worse than either
    // message alone.
    const [wsError, setWsError] = useState('');
    const [recording, setRecording] = useState(false);
    const [replayUrl, setReplayUrl] = useState(null);
    const [sampleCount, setSampleCount] = useState(0);
    const [captureWidth, setCaptureWidth] = useState(640);
    const [headDeg, setHeadDeg] = useState(20);
    const [gazeDev, setGazeDev] = useState(0.6);
    const [dwell, setDwell] = useState(2);
    const [live, setLive] = useState(null);
    const [playing, setPlaying] = useState(false);
    // Thresholds are read inside the draw loop, which must not be rebuilt on every
    // slider move — so they go through refs as well as state.
    const thr = useRef({ headDeg, gazeDev, dwell });
    thr.current = { headDeg, gazeDev, dwell };
    useEffect(() => {
        if (!config)
            return;
        setCaptureWidth(config.capture_width);
        setHeadDeg(config.head_deviation_deg);
        setGazeDev(config.gaze_deviation);
        setDwell(config.dwell_seconds);
    }, [config]);
    /** Apply the CURRENT thresholds to one sample. Pure, and the whole point. */
    const judge = useCallback((s) => {
        var _a, _b, _c, _d, _e, _f;
        if (!s)
            return null;
        const { headDeg: hLim, gazeDev: gLim } = thr.current;
        const headDev = (_b = (_a = s.head) === null || _a === void 0 ? void 0 : _a.deviation_deg) !== null && _b !== void 0 ? _b : 0;
        const gazeDeviation = (_d = (_c = s.gaze) === null || _c === void 0 ? void 0 : _c.deviation) !== null && _d !== void 0 ? _d : 0;
        const headOver = headDev > hLim;
        const gazeOver = gazeDeviation > gLim;
        let label = null;
        if (headOver) {
            label = `Head ${bearingWord((_e = s.head) === null || _e === void 0 ? void 0 : _e.bearing_deg)} — ${headDev.toFixed(1)}°`;
        }
        if (gazeOver) {
            const g = `Eyes ${bearingWord((_f = s.gaze) === null || _f === void 0 ? void 0 : _f.bearing_deg)} — ${gazeDeviation.toFixed(2)}`;
            label = label ? `${label}   ·   ${g}` : g;
        }
        return { headDev, gazeDev: gazeDeviation, headOver, gazeOver, label };
    }, []);
    /**
     * How long the current deviation has already been held, at time `t`.
     *
     * The dwell is what separates a glance from reading something, so a label
     * that ignored it would flag every head turn and teach the operator to
     * distrust the bench. Walks backwards from `t` while the same verdict holds.
     */
    const heldFor = useCallback((idx) => {
        const arr = samplesRef.current;
        if (!arr.length || idx <= 0)
            return 0;
        const now = judge(arr[idx]);
        if (!now || (!now.headOver && !now.gazeOver))
            return 0;
        let i = idx;
        while (i > 0) {
            const prev = judge(arr[i - 1]);
            if (!prev || (!prev.headOver && !prev.gazeOver))
                break;
            i -= 1;
        }
        return arr[idx].t - arr[i].t;
    }, [judge]);
    const nearestSample = useCallback((t) => {
        const arr = samplesRef.current;
        if (!arr.length)
            return -1;
        let lo = 0;
        let hi = arr.length - 1;
        while (lo < hi) {
            const mid = (lo + hi) >> 1;
            if (arr[mid].t < t)
                lo = mid + 1;
            else
                hi = mid;
        }
        return lo;
    }, []);
    // ── camera ────────────────────────────────────────────────────────────────
    const startCamera = () => __awaiter(void 0, void 0, void 0, function* () {
        setCamState('starting');
        setCamError('');
        try {
            const stream = yield navigator.mediaDevices.getUserMedia({
                video: { width: { ideal: 1280 }, height: { ideal: 720 } },
                audio: false,
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                yield videoRef.current.play();
            }
            setCamState('on');
            connectWs();
        }
        catch (e) {
            setCamState('error');
            setCamError((e === null || e === void 0 ? void 0 : e.message) || 'Could not open the camera.');
        }
    });
    const stopCamera = () => {
        var _a, _b;
        (_a = streamRef.current) === null || _a === void 0 ? void 0 : _a.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        (_b = wsRef.current) === null || _b === void 0 ? void 0 : _b.close();
        wsRef.current = null;
        setCamState('idle');
    };
    const connectWs = () => {
        const url = proctoringWsUrl(webSocketUrl);
        if (!url) {
            setWsError('No WebSocket URL configured — detection cannot run.');
            return;
        }
        try {
            const ws = new WebSocket(url);
            wsRef.current = ws;
            ws.onopen = () => {
                setWsState('ready');
                // The reconnect worked, so whatever went wrong before is history.
                setWsError('');
            };
            // Surfaced, not swallowed. A bench that records happily and detects
            // nothing, with the reason hidden in a bare catch, is worse than one
            // that refuses to start — you cannot tell a broken socket from a face
            // the detector genuinely cannot find.
            ws.onerror = () => {
                setWsState('error');
                setWsError(`Detector socket failed (${url}) — retrying.`);
            };
            ws.onclose = () => setWsState((s) => (s === 'ready' ? 'idle' : s));
            ws.onmessage = (evt) => {
                try {
                    const msg = JSON.parse(evt.data);
                    if (msg.type === 'error') {
                        setWsState('error');
                        setWsError(msg.error || 'Detector reported an error.');
                        return;
                    }
                    if (msg.type === 'ready') {
                        setWsState('ready');
                        setWsError('');
                    }
                    if (msg.type === 'result' && msg.ok) {
                        latestRef.current = msg;
                        lastResultAtRef.current = performance.now();
                    }
                }
                catch (_a) {
                    /* a malformed frame result is not worth breaking the bench for */
                }
            };
        }
        catch (e) {
            setWsState('error');
            setWsError((e === null || e === void 0 ? void 0 : e.message) || 'Could not open the detector socket.');
        }
    };
    // ── the frame pump: capture → detect → (optionally) record ───────────────
    useEffect(() => {
        if (camState !== 'on')
            return undefined;
        const id = window.setInterval(() => {
            // Re-open a socket that dropped. The bench is long-lived — an operator
            // leaves it open while adjusting a room — and a detector that dies
            // silently mid-session is indistinguishable from a face it cannot find.
            const sock = wsRef.current;
            if (!sock || sock.readyState === WebSocket.CLOSED) {
                connectWs();
                return;
            }
            const v = videoRef.current;
            const ws = wsRef.current;
            if (!v || !v.videoWidth)
                return;
            if (!frameCanvasRef.current)
                frameCanvasRef.current = document.createElement('canvas');
            const c = frameCanvasRef.current;
            // Downscale to the configured capture width — the SAME resolution the
            // live pipeline sends, so what is tuned here is what runs in an interview.
            c.width = captureWidth;
            c.height = Math.round((v.videoHeight / v.videoWidth) * captureWidth);
            const ctx = c.getContext('2d');
            if (!ctx)
                return;
            ctx.drawImage(v, 0, 0, c.width, c.height);
            if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify(Object.assign(Object.assign({ type: 'frame', frame: c.toDataURL('image/jpeg', 0.6) }, (baselineRef.current ? { baseline: baselineRef.current } : {})), { tasks: engineRef.current === 'tasks' })));
            }
            const fresh = performance.now() - lastResultAtRef.current < RESULT_STALE_MS;
            const r = fresh ? latestRef.current : null;
            if (!fresh)
                latestRef.current = null;
            if (r === null || r === void 0 ? void 0 : r.detected) {
                const sample = {
                    t: recording ? (performance.now() - recStartRef.current) / 1000 : 0,
                    face: r.face || {},
                    head: r.head_pose || {},
                    gaze: r.gaze || {},
                    pose: r.pose,
                    leftHand: r.left_hand,
                    rightHand: r.right_hand,
                    blendshapes: r.blendshapes,
                    matrix: r.head_pose_matrix,
                };
                setLive(judge(sample));
                if (recording) {
                    samplesRef.current.push(sample);
                    setSampleCount(samplesRef.current.length);
                }
            }
            else {
                setLive(null);
            }
        }, 1000 / DETECT_FPS);
        return () => window.clearInterval(id);
    }, [camState, captureWidth, recording, judge]);
    // ── record (memory only) ─────────────────────────────────────────────────
    const startRecording = () => {
        const stream = streamRef.current;
        if (!stream)
            return;
        // Recording with no detector produces a video and zero samples, which looks
        // exactly like "nothing suspicious happened". Refuse instead.
        if (wsState !== 'ready') {
            showToast({
                message: 'Detector is not connected — nothing would be measured.',
                status: 'error',
            });
            return;
        }
        // An uncentred recording measures every frame from an absolute zero, so the
        // thresholds tuned against it would be meaningless. Refuse rather than let
        // an operator spend a session on numbers that cannot be used.
        if (!baselineRef.current) {
            showToast({ message: 'Set centre before recording.', status: 'error' });
            return;
        }
        samplesRef.current = [];
        chunksRef.current = [];
        setSampleCount(0);
        if (replayUrl)
            URL.revokeObjectURL(replayUrl);
        setReplayUrl(null);
        const rec = new MediaRecorder(stream, { mimeType: 'video/webm' });
        rec.ondataavailable = (e) => {
            if (e.data.size)
                chunksRef.current.push(e.data);
        };
        rec.onstop = () => {
            const blob = new Blob(chunksRef.current, { type: 'video/webm' });
            // An object URL, not an upload. Nothing leaves the page.
            setReplayUrl(URL.createObjectURL(blob));
        };
        recorderRef.current = rec;
        recStartRef.current = performance.now();
        rec.start(250);
        setRecording(true);
    };
    const stopRecording = () => {
        var _a;
        (_a = recorderRef.current) === null || _a === void 0 ? void 0 : _a.stop();
        recorderRef.current = null;
        setRecording(false);
    };
    // Revoke the object URL on unmount so a long admin session does not hold
    // every recording it ever made in memory.
    useEffect(() => () => {
        if (replayUrl)
            URL.revokeObjectURL(replayUrl);
    }, [replayUrl]);
    // ── draw: mesh + label, live or on playback ──────────────────────────────
    useEffect(() => {
        let raf = 0;
        const draw = () => {
            var _a, _b, _c, _d;
            raf = requestAnimationFrame(draw);
            const cv = canvasRef.current;
            const src = replayUrl ? replayRef.current : videoRef.current;
            if (!cv || !src || !src.videoWidth)
                return;
            const rect = src.getBoundingClientRect();
            if (cv.width !== Math.round(rect.width))
                cv.width = Math.round(rect.width);
            if (cv.height !== Math.round(rect.height))
                cv.height = Math.round(rect.height);
            const ctx = cv.getContext('2d');
            if (!ctx)
                return;
            ctx.clearRect(0, 0, cv.width, cv.height);
            let sample = null;
            let held = 0;
            if (replayUrl) {
                const i = nearestSample(src.currentTime);
                if (i >= 0) {
                    sample = samplesRef.current[i];
                    held = heldFor(i);
                }
            }
            else if ((_a = latestRef.current) === null || _a === void 0 ? void 0 : _a.detected) {
                const r = latestRef.current;
                // Every stream, not just the face. An earlier version built this one by
                // hand with three fields while the RECORDING path carried all of them,
                // so the body and hands were detected, sent, stored — and never drawn
                // live. Two places constructing the same object is how that happens.
                sample = {
                    t: 0,
                    face: r.face || {},
                    head: r.head_pose || {},
                    gaze: r.gaze || {},
                    pose: r.pose,
                    leftHand: r.left_hand,
                    rightHand: r.right_hand,
                    blendshapes: r.blendshapes,
                    matrix: r.head_pose_matrix,
                };
            }
            if (!sample)
                return;
            // Landmarks are normalized to the camera frame, and the camera fills this
            // box — no tile, no crop, so the mapping is a plain multiply. That is the
            // whole reason this bench no longer draws a fake screen.
            const L = layersRef.current;
            // Map into the video's CONTENT rect, not the element's box.
            //
            // The <video> is `object-contain`, so a 16:9 stream in a 1.54:1 box is
            // letterboxed — measured here as 1228x799 holding 1228x690 of picture,
            // with 54px bars. Landmarks are normalised to the CAMERA frame, so
            // multiplying by the element height stretches every one of them ~16%
            // vertically and drifts them further the closer they are to the edges.
            // The report overlay learned this the hard way (VA-28145); the same
            // arithmetic applies here.
            const scale = Math.min(cv.width / src.videoWidth, cv.height / src.videoHeight);
            const contentW = src.videoWidth * scale;
            const contentH = src.videoHeight * scale;
            const offX = (cv.width - contentW) / 2;
            const offY = (cv.height - contentH) / 2;
            const X = (p) => offX + p[0] * contentW;
            const Y = (p) => offY + p[1] * contentH;
            const edges = (map, pairs, colour, width) => {
                if (!map)
                    return;
                ctx.strokeStyle = colour;
                ctx.lineWidth = width;
                pairs.forEach(([a, b]) => {
                    const pa = map[String(a)];
                    const pb = map[String(b)];
                    if (!pa || !pb)
                        return;
                    ctx.beginPath();
                    ctx.moveTo(X(pa), Y(pa));
                    ctx.lineTo(X(pb), Y(pb));
                    ctx.stroke();
                });
            };
            // ── face mesh ────────────────────────────────────────────────────────
            // 478 points, not 35 — drawn small and semi-transparent. At the old
            // radius a full mesh becomes a solid mask and hides the face underneath,
            // which is the thing the operator is trying to look at.
            if (L.mesh) {
                const pts = Object.values(sample.face);
                ctx.fillStyle = pts.length > 100 ? 'rgba(34,211,238,0.65)' : '#22d3ee';
                const r = pts.length > 100 ? 0.9 : 1.8;
                pts.forEach((p) => {
                    if (!p || p.length < 2)
                        return;
                    ctx.beginPath();
                    ctx.arc(X(p), Y(p), r, 0, Math.PI * 2);
                    ctx.fill();
                });
            }
            // ── irises ───────────────────────────────────────────────────────────
            // Where the iris sits inside the eye IS the gaze signal, so it gets its
            // own colour rather than disappearing into the mesh.
            if (L.iris) {
                ctx.strokeStyle = '#fbbf24';
                ctx.lineWidth = 1.5;
                [LEFT_IRIS, RIGHT_IRIS].forEach((ring) => {
                    const pts = ring.map((i) => sample.face[String(i)]).filter(Boolean);
                    if (pts.length < 4)
                        return;
                    const cx = pts.reduce((a, p) => a + X(p), 0) / pts.length;
                    const cy = pts.reduce((a, p) => a + Y(p), 0) / pts.length;
                    const rad = Math.max(...pts.map((p) => Math.hypot(X(p) - cx, Y(p) - cy)));
                    ctx.beginPath();
                    ctx.arc(cx, cy, Math.max(rad, 2), 0, Math.PI * 2);
                    ctx.stroke();
                });
            }
            // ── body + hands ─────────────────────────────────────────────────────
            // `slouching` and `hand_near_face` have been written and wired for months
            // with nothing feeding them. Drawing what now does is the quickest way to
            // see whether the data is good enough to switch them on.
            if (L.pose)
                edges(sample.pose, POSE_EDGES, 'rgba(74,222,128,0.9)', 2.5);
            if (L.hands) {
                edges(sample.leftHand, HAND_EDGES, 'rgba(244,114,182,0.9)', 1.8);
                edges(sample.rightHand, HAND_EDGES, 'rgba(168,85,247,0.9)', 1.8);
            }
            // ── gaze vector ──────────────────────────────────────────────────────
            if (L.gaze && ((_b = sample.gaze) === null || _b === void 0 ? void 0 : _b.gaze_x) != null) {
                const eyes = [LEFT_IRIS, RIGHT_IRIS].map((ring) => {
                    const pts = ring.map((i) => sample.face[String(i)]).filter(Boolean);
                    if (!pts.length)
                        return null;
                    return [
                        pts.reduce((a, p) => a + X(p), 0) / pts.length,
                        pts.reduce((a, p) => a + Y(p), 0) / pts.length,
                    ];
                }).filter(Boolean);
                ctx.strokeStyle = '#f59e0b';
                ctx.lineWidth = 2;
                const vecLen = contentW * 0.07;
                eyes.forEach(([ex, ey]) => {
                    ctx.beginPath();
                    ctx.moveTo(ex, ey);
                    ctx.lineTo(ex + (sample.gaze.gaze_x || 0) * vecLen, ey + (sample.gaze.gaze_y || 0) * vecLen);
                    ctx.stroke();
                });
            }
            // ── the threshold dial ───────────────────────────────────────────────
            //
            // The most useful thing on this canvas, and the reason the detection
            // model became radial. A ring drawn AT the current threshold, with a dot
            // at the live (deviation, bearing): you watch the dot cross the ring as
            // you move, and watch the ring resize as you drag the slider. A number in
            // a sidebar cannot show you where the line actually falls.
            const vv = judge(sample);
            if (L.dial && vv) {
                const R = 58;
                const cx = cv.width - R - 22;
                const cy = R + 22;
                const { headDeg: hLim } = thr.current;
                ctx.save();
                ctx.fillStyle = 'rgba(0,0,0,0.45)';
                ctx.beginPath();
                ctx.arc(cx, cy, R + 8, 0, Math.PI * 2);
                ctx.fill();
                // The threshold ring itself.
                ctx.strokeStyle = 'rgba(255,255,255,0.75)';
                ctx.lineWidth = 1.5;
                ctx.setLineDash([4, 4]);
                ctx.beginPath();
                ctx.arc(cx, cy, R, 0, Math.PI * 2);
                ctx.stroke();
                ctx.setLineDash([]);
                // Centre, and the axes, so a bearing can be read off by eye.
                ctx.strokeStyle = 'rgba(255,255,255,0.2)';
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(cx - R, cy);
                ctx.lineTo(cx + R, cy);
                ctx.moveTo(cx, cy - R);
                ctx.lineTo(cx, cy + R);
                ctx.stroke();
                // Where the head actually is. Scaled so the ring always sits at the
                // threshold, whatever it is set to — the dot's distance from centre is
                // then directly readable as "how close to flagging".
                const dev = vv.headDev;
                const bearing = (((_d = (_c = sample.head) === null || _c === void 0 ? void 0 : _c.bearing_deg) !== null && _d !== void 0 ? _d : 0) * Math.PI) / 180;
                const rr = Math.min((dev / Math.max(hLim, 0.001)) * R, R + 14);
                const dx = cx + Math.sin(bearing) * rr;
                const dy = cy - Math.cos(bearing) * rr;
                ctx.fillStyle = vv.headOver ? '#ef4444' : '#4ade80';
                ctx.beginPath();
                ctx.arc(dx, dy, 5, 0, Math.PI * 2);
                ctx.fill();
                ctx.fillStyle = 'rgba(255,255,255,0.85)';
                ctx.font = '600 10px ui-monospace, monospace';
                ctx.textAlign = 'center';
                ctx.fillText(`${dev.toFixed(0)}° / ${hLim}°`, cx, cy + R + 18);
                ctx.restore();
            }
            const v = vv;
            if (!(v === null || v === void 0 ? void 0 : v.label))
                return;
            // Only label once the deviation has been HELD for the dwell. Below it,
            // show the same text dimmed — an operator tuning a threshold needs to see
            // the near-misses, not just the hits.
            const met = held >= thr.current.dwell;
            const text = met ? `${v.label}   held ${held.toFixed(1)}s` : `${v.label}   (${held.toFixed(1)}s)`;
            ctx.font = '600 14px ui-sans-serif, system-ui, sans-serif';
            const w = ctx.measureText(text).width + 20;
            ctx.fillStyle = met ? 'rgba(220,38,38,0.92)' : 'rgba(120,113,108,0.75)';
            ctx.fillRect((cv.width - w) / 2, cv.height - 52, w, 30);
            ctx.fillStyle = '#fff';
            ctx.textAlign = 'center';
            ctx.fillText(text, cv.width / 2, cv.height - 32);
        };
        raf = requestAnimationFrame(draw);
        return () => cancelAnimationFrame(raf);
    }, [replayUrl, judge, heldFor, nearestSample]);
    // What would change if this were saved now. Drives both the disabled state of
    // Save and the confirmation's contents, so the two cannot disagree about
    // whether there is anything to apply.
    const pending = useMemo(() => thresholdDiff(config, { headDeg, gazeDev, dwell, captureWidth }), [config, headDeg, gazeDev, dwell, captureWidth]);
    const [confirming, setConfirming] = useState(false);
    const commit = () => {
        updateConfig.mutate({
            captureWidth,
            headDeviationDeg: headDeg,
            gazeDeviation: gazeDev,
            dwellSeconds: dwell,
        }, {
            onSuccess: () => {
                setConfirming(false);
                showToast({ message: 'Proctoring thresholds saved', status: 'success' });
            },
            onError: () => {
                setConfirming(false);
                showToast({ message: 'Could not save thresholds', status: 'error' });
            },
        });
    };
    /** Capture the current pose as centre. */
    const setCentre = () => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        const r = latestRef.current;
        if (!(r === null || r === void 0 ? void 0 : r.detected) || !r.head_pose) {
            showToast({ message: 'No face detected — look at the camera first.', status: 'error' });
            return;
        }
        // The RAW pose, not the relative one: the relative values are already
        // measured against whatever baseline was in force, so storing those would
        // re-centre on the last centre and drift a little further every press.
        baselineRef.current = {
            yaw: (_a = r.head_pose.yaw_deg) !== null && _a !== void 0 ? _a : 0,
            pitch: (_b = r.head_pose.pitch_deg) !== null && _b !== void 0 ? _b : 0,
            roll: (_c = r.head_pose.roll_deg) !== null && _c !== void 0 ? _c : 0,
            gaze_x: ((_e = (_d = r.gaze) === null || _d === void 0 ? void 0 : _d.gaze_x) !== null && _e !== void 0 ? _e : 0) + ((_g = (_f = baselineRef.current) === null || _f === void 0 ? void 0 : _f.gaze_x) !== null && _g !== void 0 ? _g : 0),
            gaze_y: ((_j = (_h = r.gaze) === null || _h === void 0 ? void 0 : _h.gaze_y) !== null && _j !== void 0 ? _j : 0) + ((_l = (_k = baselineRef.current) === null || _k === void 0 ? void 0 : _k.gaze_y) !== null && _l !== void 0 ? _l : 0),
        };
        setCentred(true);
        showToast({ message: 'Centre set — deviations are now measured from here.', status: 'success' });
    };
    const resetToSaved = () => {
        if (!config)
            return;
        setCaptureWidth(config.capture_width);
        setHeadDeg(config.head_deviation_deg);
        setGazeDev(config.gaze_deviation);
        setDwell(config.dwell_seconds);
    };
    const flagged = useMemo(() => {
        if (!replayUrl || !samplesRef.current.length)
            return null;
        let n = 0;
        samplesRef.current.forEach((_, i) => {
            const v = judge(samplesRef.current[i]);
            if (v && (v.headOver || v.gazeOver) && heldFor(i) >= thr.current.dwell)
                n += 1;
        });
        return n;
    }, [replayUrl, headDeg, gazeDev, dwell, judge, heldFor]);
    const slider = (label, value, min, max, step, set, suffix = '') => (_jsxs("div", { className: "mb-4", children: [_jsxs("div", { className: "mb-1 flex items-baseline justify-between", children: [_jsx("span", { className: "text-xs font-medium text-dash-text2", children: label }), _jsxs("span", { className: "font-mono text-xs text-dash-text", children: [value, suffix] })] }), _jsx("input", { type: "range", min: min, max: max, step: step, value: value, onChange: (e) => set(Number(e.target.value)), className: "w-full accent-blue-600" })] }));
    return (_jsxs("div", { className: "flex h-full gap-4", children: [confirming && pending.length > 0 && (_jsx(ConfirmThresholds, { rows: pending, onCancel: () => setConfirming(false), onConfirm: commit, isSaving: updateConfig.isPending })), _jsxs("div", { className: "w-[320px] shrink-0 overflow-y-auto rounded-xl border border-dash-border bg-dash-card p-4", children: [_jsx("h3", { className: "mb-1 text-sm font-semibold text-dash-text", children: "Threshold calibration" }), _jsx("p", { className: "mb-4 text-xs leading-5 text-dash-muted", children: "Record a short session, then move the thresholds against the playback to see what each setting would flag. Nothing is uploaded \u2014 only the thresholds are saved." }), camState !== 'on' ? (_jsxs(Button, { onClick: startCamera, disabled: camState === 'starting', className: "w-full", children: [camState === 'starting'
                                ? _jsx(Loader2, { className: "mr-2 h-4 w-4 animate-spin" })
                                : _jsx(Camera, { className: "mr-2 h-4 w-4" }), "Start camera"] })) : (_jsxs("div", { className: "flex gap-2", children: [!recording ? (_jsx(Button, { onClick: startRecording, className: "flex-1", children: "Record" })) : (_jsxs(Button, { onClick: stopRecording, className: "flex-1 bg-red-600 hover:bg-red-700", children: [_jsx(Square, { className: "mr-2 h-3.5 w-3.5" }), " Stop (", sampleCount, ")"] })), _jsx(Button, { variant: "outline", onClick: stopCamera, children: "Off" })] })), camError && _jsx("p", { className: "mt-2 text-xs text-red-600", children: camError }), wsError && wsState !== 'ready' && (_jsx("p", { className: "mt-2 text-xs text-red-600", children: wsError })), camState === 'on' && (_jsxs(Button, { variant: "outline", onClick: setCentre, className: "mt-2 w-full", title: "Look straight at the camera, then press this", children: [_jsx(Crosshair, { className: "mr-2 h-4 w-4" }), centred ? 'Re-set centre' : 'Set centre'] })), camState === 'on' && !centred && (_jsxs("p", { className: "mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800", children: ["Look straight at the camera and press ", _jsx("strong", { children: "Set centre" }), " first. Until then every reading is measured from an absolute zero nobody sits at, so a face at a normal angle reads as a large deviation."] })), camState === 'on' && (_jsxs("p", { className: "mt-2 flex items-center gap-1.5 text-xs", children: [_jsx("span", { className: `inline-block h-2 w-2 rounded-full ${wsState === 'ready' ? 'bg-green-500'
                                    : wsState === 'error' ? 'bg-red-500' : 'bg-amber-400'}` }), _jsxs("span", { className: "text-dash-text2", children: ["detector ", wsState === 'ready' ? 'connected' : wsState === 'error' ? 'FAILED' : 'connecting…', wsState === 'ready' && ` · ${Object.keys(((_a = latestRef.current) === null || _a === void 0 ? void 0 : _a.face) || {}).length} points`] })] })), _jsx("hr", { className: "my-4 border-dash-border" }), _jsx("label", { className: "mb-1 block text-xs font-medium text-dash-text2", children: "Engine" }), _jsx("div", { className: "mb-3 flex gap-1 rounded-lg bg-dash-active p-1", children: ['tasks', 'legacy'].map((e) => (_jsx("button", { type: "button", onClick: () => setEngine(e), className: `flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${engine === e
                                ? 'bg-dash-accent text-white shadow-sm'
                                : 'text-dash-text2 hover:text-dash-text'}`, children: e === 'tasks' ? 'Tasks (+body)' : 'Legacy' }, e))) }), _jsx("label", { className: "mb-1 block text-xs font-medium text-dash-text2", children: "Layers" }), _jsx("div", { className: "mb-4 grid grid-cols-2 gap-x-2 gap-y-1", children: Object.keys(layers).map((k) => (_jsxs("label", { className: "flex items-center gap-1.5 text-xs text-dash-text2", children: [_jsx("input", { type: "checkbox", checked: layers[k], onChange: (ev) => setLayers((s2) => (Object.assign(Object.assign({}, s2), { [k]: ev.target.checked }))) }), k] }, k))) }), _jsx("label", { className: "mb-1 block text-xs font-medium text-dash-text2", children: "Camera capture width" }), _jsx("select", { value: captureWidth, onChange: (e) => setCaptureWidth(Number(e.target.value)), className: "mb-4 w-full rounded-lg border border-dash-border px-2 py-1.5 text-sm", children: WIDTH_OPTIONS.map((w) => _jsxs("option", { value: w, children: [w, "px"] }, w)) }), slider('Head deviation', headDeg, (_b = config === null || config === void 0 ? void 0 : config.head_deviation_deg_min) !== null && _b !== void 0 ? _b : 2, (_c = config === null || config === void 0 ? void 0 : config.head_deviation_deg_max) !== null && _c !== void 0 ? _c : 90, 0.5, setHeadDeg, '°'), slider('Eye deviation', gazeDev, (_d = config === null || config === void 0 ? void 0 : config.gaze_deviation_min) !== null && _d !== void 0 ? _d : 0.02, (_e = config === null || config === void 0 ? void 0 : config.gaze_deviation_max) !== null && _e !== void 0 ? _e : 2, 0.01, setGazeDev), slider('Must hold for', dwell, (_f = config === null || config === void 0 ? void 0 : config.dwell_seconds_min) !== null && _f !== void 0 ? _f : 0.2, (_g = config === null || config === void 0 ? void 0 : config.dwell_seconds_max) !== null && _g !== void 0 ? _g : 15, 0.1, setDwell, 's'), flagged != null && (_jsxs("p", { className: "mb-3 rounded-lg bg-dash-active px-3 py-2 text-xs text-dash-text2", children: [_jsx("strong", { children: flagged }), " of ", samplesRef.current.length, " recorded frames would be flagged at these settings."] })), _jsxs("div", { className: "flex gap-2", children: [_jsxs(Button, { onClick: () => setConfirming(true), disabled: updateConfig.isPending || pending.length === 0, title: pending.length === 0 ? 'Nothing to save — these are the stored values' : undefined, className: "flex-1", children: [_jsx(Save, { className: "mr-2 h-4 w-4" }), pending.length > 0 ? `Save (${pending.length})` : 'Save'] }), _jsx(Button, { variant: "outline", onClick: resetToSaved, title: "Back to the saved values", children: _jsx(RotateCcw, { className: "h-4 w-4" }) })] }), _jsxs("div", { className: "mt-4 rounded-lg bg-dash-surface border border-dash-border p-3 font-mono text-[11px] leading-5 text-dash-text", children: [_jsx("div", { className: "mb-1 text-dash-muted", children: "measured now" }), live ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: live.headOver ? 'text-red-400' : '', children: ["head ", live.headDev.toFixed(1), "\u00B0 ", bearingWord(undefined)] }), _jsxs("div", { className: live.gazeOver ? 'text-red-400' : '', children: ["eyes ", live.gazeDev.toFixed(2)] })] })) : _jsx("div", { className: "text-dash-muted", children: "no face" }), engine === 'tasks' && ((_h = latestRef.current) === null || _h === void 0 ? void 0 : _h.blendshapes) && (_jsxs("div", { className: "mt-2 border-t border-dash-border pt-2", children: [[
                                        ['squint L/R', 'eyeSquintLeft', 'eyeSquintRight'],
                                        ['blink L/R', 'eyeBlinkLeft', 'eyeBlinkRight'],
                                        ['look in/out', 'eyeLookInLeft', 'eyeLookOutLeft'],
                                        ['jaw open', 'jawOpen', null],
                                    ].map(([label, a, b]) => {
                                        var _a, _b, _c;
                                        const bs = ((_a = latestRef.current) === null || _a === void 0 ? void 0 : _a.blendshapes) || {};
                                        const va = (_b = bs[a]) !== null && _b !== void 0 ? _b : 0;
                                        const vb = b ? (_c = bs[b]) !== null && _c !== void 0 ? _c : 0 : null;
                                        return (_jsxs("div", { className: "flex justify-between", children: [_jsx("span", { className: "text-dash-muted", children: label }), _jsxs("span", { children: [va.toFixed(2), vb != null ? ` / ${vb.toFixed(2)}` : ''] })] }, label));
                                    }), ((_j = latestRef.current) === null || _j === void 0 ? void 0 : _j.head_pose_matrix) && (_jsxs("div", { className: "mt-1 text-dash-muted", children: ["matrix y/p/r", ' ', _jsxs("span", { className: "text-dash-text", children: [(_k = latestRef.current.head_pose_matrix.yaw_deg) === null || _k === void 0 ? void 0 : _k.toFixed(0), "/", (_l = latestRef.current.head_pose_matrix.pitch_deg) === null || _l === void 0 ? void 0 : _l.toFixed(0), "/", (_m = latestRef.current.head_pose_matrix.roll_deg) === null || _m === void 0 ? void 0 : _m.toFixed(0)] })] })), _jsxs("div", { className: "mt-1 text-dash-muted", children: [(_o = latestRef.current) === null || _o === void 0 ? void 0 : _o.elapsed_ms, "ms \u00B7", ' ', Object.keys(((_p = latestRef.current) === null || _p === void 0 ? void 0 : _p.pose) || {}).length, " pose \u00B7", ' ', Object.keys(((_q = latestRef.current) === null || _q === void 0 ? void 0 : _q.left_hand) || {}).length
                                                + Object.keys(((_r = latestRef.current) === null || _r === void 0 ? void 0 : _r.right_hand) || {}).length, " hand"] })] }))] })] }), _jsxs("div", { className: "relative flex-1 overflow-hidden rounded-xl bg-black", children: [_jsx("video", { ref: videoRef, muted: true, playsInline: true, className: `h-full w-full object-contain ${replayUrl ? 'hidden' : ''}` }), _jsx("video", { ref: replayRef, src: replayUrl !== null && replayUrl !== void 0 ? replayUrl : undefined, playsInline: true, controls: true, onPlay: () => setPlaying(true), onPause: () => setPlaying(false), className: `h-full w-full object-contain ${replayUrl ? '' : 'hidden'}` }), _jsx("canvas", { ref: canvasRef, className: "pointer-events-none absolute left-0 top-0" }), replayUrl && (_jsx("button", { type: "button", onClick: () => {
                            const v = replayRef.current;
                            if (!v)
                                return;
                            if (v.paused)
                                void v.play();
                            else
                                v.pause();
                        }, className: "absolute left-3 top-3 rounded-full bg-black/70 p-2 text-white", children: playing ? _jsx(Pause, { className: "h-4 w-4" }) : _jsx(Play, { className: "h-4 w-4" }) }))] })] }));
};
export default ProctoringCalibrationPanel;
